import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Bell, Check, CheckCheck, X, Package, Truck, 
  CreditCard, AlertCircle, UserPlus, MessageSquare, Clock,
  ChevronRight, Loader2, Wifi, WifiOff
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

// Notification type icons and colors
const NOTIFICATION_CONFIG = {
  order_confirmed: { icon: Package, color: 'text-green-500', bg: 'bg-green-100' },
  order_preparing: { icon: Clock, color: 'text-orange-500', bg: 'bg-orange-100' },
  order_ready: { icon: Check, color: 'text-green-500', bg: 'bg-green-100' },
  order_out_for_delivery: { icon: Truck, color: 'text-blue-500', bg: 'bg-blue-100' },
  order_delivered: { icon: CheckCheck, color: 'text-green-500', bg: 'bg-green-100' },
  driver_arriving: { icon: Truck, color: 'text-blue-500', bg: 'bg-blue-100' },
  new_order: { icon: Package, color: 'text-orange-500', bg: 'bg-orange-100' },
  new_delivery: { icon: Truck, color: 'text-green-500', bg: 'bg-green-100' },
  payout_processed: { icon: CreditCard, color: 'text-green-500', bg: 'bg-green-100' },
  earnings_payout: { icon: CreditCard, color: 'text-green-500', bg: 'bg-green-100' },
  account_approved: { icon: UserPlus, color: 'text-green-500', bg: 'bg-green-100' },
  new_signup: { icon: UserPlus, color: 'text-blue-500', bg: 'bg-blue-100' },
  support_ticket: { icon: MessageSquare, color: 'text-purple-500', bg: 'bg-purple-100' },
  system_alert: { icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-100' },
  admin_broadcast: { icon: Bell, color: 'text-orange-500', bg: 'bg-orange-100' },
  table_order: { icon: Package, color: 'text-orange-500', bg: 'bg-orange-100', action: '/restaurant/live-tables' },
  waiter_request: { icon: Bell, color: 'text-red-500', bg: 'bg-red-100', action: '/restaurant/live-tables' },
  booking: { icon: Clock, color: 'text-blue-500', bg: 'bg-blue-100', action: '/restaurant/tables' },
  bill: { icon: CreditCard, color: 'text-green-500', bg: 'bg-green-100' },
  default: { icon: Bell, color: 'text-slate-500', bg: 'bg-slate-100' },
};

export function NotificationCenter({ className = '' }) {
  const { api, token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (!token || wsRef.current?.readyState === WebSocket.OPEN) return;

    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
    if (!wsUrl) return;

    try {
      wsRef.current = new WebSocket(`${wsUrl}/ws/notifications?token=${token}`);

      wsRef.current.onopen = () => {
        console.log('Notification WebSocket connected');
        setIsConnected(true);
        
        // Start ping interval
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'init') {
            setUnreadCount(data.unread_count || 0);
          } else if (data.type === 'notification') {
            // New notification received
            const notification = data.data;
            setNotifications(prev => [notification, ...prev.slice(0, 9)]);
            setUnreadCount(prev => prev + 1);
            
            // Show toast notification
            toast(notification.title, {
              description: notification.message,
              action: notification.action_url ? {
                label: 'View',
                onClick: () => window.location.href = notification.action_url
              } : undefined
            });
          } else if (data.type === 'notification_read') {
            setNotifications(prev => 
              prev.map(n => n.id === data.notification_id ? { ...n, read: true } : n)
            );
          } else if (data.type === 'all_read') {
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
          } else if (data.type === 'unread_count') {
            setUnreadCount(data.count || 0);
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      wsRef.current.onclose = () => {
        console.log('Notification WebSocket disconnected');
        setIsConnected(false);
        clearInterval(pingIntervalRef.current);
        
        // Reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, 5000);
      };

      wsRef.current.onerror = (error) => {
        console.error('Notification WebSocket error:', error);
        setIsConnected(false);
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }, [token]);

  // Connect WebSocket on mount
  useEffect(() => {
    connectWebSocket();
    
    return () => {
      clearTimeout(reconnectTimeoutRef.current);
      clearInterval(pingIntervalRef.current);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connectWebSocket]);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/notifications?limit=10');
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Fallback: Fetch unread count periodically if WebSocket not connected
  useEffect(() => {
    if (isConnected) return;
    
    const fetchUnreadCount = async () => {
      try {
        const response = await api.get('/notifications/unread-count');
        setUnreadCount(response.data.unread_count || 0);
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      }
    };
    
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [api, isConnected]);

  // Fetch full notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      // Send via WebSocket if connected
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'mark_read', notification_id: notificationId }));
      } else {
        await api.post(`/notifications/${notificationId}/read`);
      }
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      // Send via WebSocket if connected
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'mark_all_read' }));
      } else {
        await api.post('/notifications/read-all');
      }
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const deleteNotification = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      // Refresh unread count
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'get_unread_count' }));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    // Use action_url from notification, or fallback to config action
    const config = getNotificationConfig(notification.type);
    const actionUrl = notification.action_url || config.action;
    
    if (actionUrl) {
      window.location.href = actionUrl;
    }
    setIsOpen(false);
  };

  const getNotificationConfig = (type) => {
    return NOTIFICATION_CONFIG[type] || NOTIFICATION_CONFIG.default;
  };

  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return '';
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className={`relative h-12 w-12 ${className}`}
          data-testid="notification-bell"
        >
          <Bell className="w-7 h-7" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white text-sm font-bold rounded-full flex items-center justify-center animate-pulse shadow-lg">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
          {/* Connection indicator - bigger and more visible */}
          <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${isConnected ? 'bg-green-500' : 'bg-slate-400'}`} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-80 max-h-[480px] overflow-hidden flex flex-col"
        data-testid="notification-dropdown"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
          <div className="flex items-center gap-2">
            <div>
              <h3 className="font-semibold text-slate-900">Notifications</h3>
              {unreadCount > 0 && (
                <p className="text-xs text-slate-500">{unreadCount} unread</p>
              )}
            </div>
            {isConnected ? (
              <Wifi className="w-3 h-3 text-green-500" title="Real-time connected" />
            ) : (
              <WifiOff className="w-3 h-3 text-slate-400" title="Connecting..." />
            )}
          </div>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={markAllAsRead}
              className="text-xs text-orange-600 hover:text-orange-700"
            >
              <CheckCheck className="w-3 h-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>

        {/* Notifications List */}
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : notifications.length > 0 ? (
            notifications.map((notification) => {
              const config = getNotificationConfig(notification.type);
              const Icon = config.icon;
              
              return (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`flex items-start gap-3 px-4 py-3 hover:bg-slate-50 cursor-pointer border-b border-slate-100 transition-colors ${
                    !notification.read ? 'bg-orange-50/50' : ''
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${config.bg}`}>
                    <Icon className={`w-5 h-5 ${config.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm font-medium text-slate-900 truncate ${!notification.read ? 'font-semibold' : ''}`}>
                        {notification.title}
                      </p>
                      <button
                        onClick={(e) => deleteNotification(notification.id, e)}
                        className="text-slate-400 hover:text-red-500 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">
                      {notification.message}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {formatTime(notification.created_at)}
                    </p>
                  </div>
                  {!notification.read && (
                    <div className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0 mt-2" />
                  )}
                </div>
              );
            })
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <Bell className="w-10 h-10 mb-3 opacity-50" />
              <p className="text-sm">No notifications yet</p>
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="border-t bg-slate-50">
            <Button 
              variant="ghost" 
              className="w-full text-sm text-slate-600 hover:text-orange-600"
              onClick={() => window.location.href = '/notifications'}
            >
              View all notifications
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default NotificationCenter;
